import React, { useEffect, useState, useRef } from 'react';
import {
  StyleSheet,
  View,
  TouchableOpacity,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Switch,
} from 'react-native';
import { ActivityIndicator, Text, Checkbox, Button, TextInput as PaperTextInput } from 'react-native-paper';
import { useAppColors } from '../../hooks/useAppColors';
import { useAuthStore, AuthUser } from '../../stores/authStore';
import { useRoleStore } from '../../stores/roleStore';
import { usersApi, AuthSyncPayload } from '../../api/users';
import { NEON_AUTH_URL } from '../../utils/constants';
import { authClient } from '../../lib/auth-client';
import { useTranslation } from 'react-i18next';
import { getApiError, weightToKg, heightToCm } from '../../utils/formatters';
import { useUpdateProfile } from '../../hooks/useAuth';
import { paymentsApi } from '../../api/payments';
import * as SecureStore from 'expo-secure-store';
import { Platform as RNPlatform } from 'react-native';
import DatePicker from '../../components/DatePicker';
import { isAdult } from '../../utils/helpers';

const isWeb = RNPlatform.OS === 'web';

const COOKIE_STORE_KEY = 'cbjj_cookie';

// Web-safe storage helpers
const storage = {
  getItem: (key: string): string | null => {
    if (isWeb) return localStorage.getItem(key);
    return SecureStore.getItem(key);
  },
  setItem: (key: string, value: string): void => {
    if (isWeb) localStorage.setItem(key, value);
    else SecureStore.setItem(key, value);
  },
};

function getStoredCookieString(): string {
  const raw = storage.getItem(COOKIE_STORE_KEY) || '{}';
  try {
    const parsed = JSON.parse(raw);
    return Object.entries(parsed).reduce((acc: string, [key, val]: [string, any]) => {
      if (val.expires && new Date(val.expires) < new Date()) return acc;
      return acc ? `${acc}; ${key}=${val.value}` : `${key}=${val.value}`;
    }, '');
  } catch {
    return '';
  }
}

async function getNeonJwt(): Promise<string> {
  const headers: Record<string, string> = {};
  if (!isWeb) {
    const cookieStr = getStoredCookieString();
    if (cookieStr) headers['Cookie'] = cookieStr;
  }
  const res = await fetch(`${NEON_AUTH_URL}/token`, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
  if (!res.ok) throw new Error(`Failed to get Neon JWT: ${res.status}`);
  const data = await res.json();
  if (!data.token) throw new Error('No token in /token response');
  return data.token;
}

function persistCookies(setCookieHeader: string) {
  const { getSetCookie } = require('@better-auth/expo/client');
  const prev = storage.getItem(COOKIE_STORE_KEY) ?? undefined;
  const merged = getSetCookie(setCookieHeader, prev);
  storage.setItem(COOKIE_STORE_KEY, merged);
}

type Step = 'processing' | 'needs_email_verification' | 'needs_completion' | 'completing' | 'done' | 'error';

interface SyncResult {
  access_token: string;
  user: AuthUser;
  is_new_user: boolean;
}

export default function AuthCallbackScreen({ navigation, route }: any) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const setAuth = useAuthStore((s) => s.setAuth);
  const setNeedsOnboarding = useAuthStore((s) => s.setNeedsOnboarding);
  const setNeedsSubscription = useAuthStore((s) => s.setNeedsSubscription);
  const setEmailVerified = useAuthStore((s) => s.setEmailVerified);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const needsOnboarding = useAuthStore((s) => s.needsOnboarding);
  const authUser = useAuthStore((s) => s.user);
  const authToken = useAuthStore((s) => s.token);
  const setActiveRole = useRoleStore((s) => s.setActiveRole);
  const updateProfile = useUpdateProfile();

  const [step, setStep] = useState<Step>('processing');
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nameError, setNameError] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const [termsChecked, setTermsChecked] = useState(false);
  const [dobError, setDobError] = useState('');
  const [weightInput, setWeightInput] = useState('');
  const [heightInput, setHeightInput] = useState('');
  const [unitSystem, setUnitSystem] = useState<'metric' | 'imperial'>('metric');
  const processedRef = useRef(false);

  // If user already has a DOB (pre-provisioned sub-user from invite flow),
  // don't show the DatePicker — it's already set by the guardian.
  const isDobPrefilled = !!(syncResult?.user?.dob || authUser?.dob);

  // Process OAuth callback on mount (web: from URL params, native: from nav params)
  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const processCallback = async () => {
      try {
        // ── Case 1: User is already authenticated but needs onboarding ──
        // This happens on page refresh when needsOnboarding was persisted.
        // Skip OAuth processing and go straight to profile completion.
        if (isAuthenticated && needsOnboarding && authToken && authUser) {
          setSyncResult({
            access_token: authToken,
            user: authUser,
            is_new_user: true,
          });
          if (authUser.dob) {
            try { setDateOfBirth(new Date(authUser.dob)); } catch {}
          }
          if (authUser.first_name) setFirstName(authUser.first_name);
          if (authUser.last_name) setLastName(authUser.last_name);
          setStep('needs_completion');
          return;
        }

        // ── Case 2: Fresh OAuth callback ──
        let result: SyncResult;

        // Check if we already have a sync result (native flow)
        const navSyncResult = route.params?.syncResult as SyncResult | undefined;
        if (navSyncResult) {
          result = navSyncResult;
        } else if (isWeb) {
          // Web flow: extract verifier from URL
          const url = new URL(window.location.href);
          const verifier = url.searchParams.get('neon_auth_session_verifier');

          if (!verifier) {
            // No verifier and not already authed → stale refresh, go to login
            clearAuth();
            setStep('error');
            setError(t('auth.callbackNoVerifier'));
            return;
          }

          // Clean up URL
          url.searchParams.delete('neon_auth_session_verifier');
          window.history.replaceState({}, '', url.toString());

          // Exchange verifier for session
          const sessionRes = await fetch(
            `${NEON_AUTH_URL}/get-session?neon_auth_session_verifier=${encodeURIComponent(verifier)}`,
            { method: 'GET', credentials: 'include' },
          );

          if (!sessionRes.ok) {
            const err = await sessionRes.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to exchange verifier');
          }

          // Persist any cookies from the session response
          const sessionSetCookie = sessionRes.headers.get('set-cookie');
          if (sessionSetCookie) persistCookies(sessionSetCookie);

          // Get JWT and sync with backend (include invite token if sub-user invite flow)
          const jwt = await getNeonJwt();
          const inviteToken = useAuthStore.getState().pending_invite_token;
          const syncData = await usersApi.authSync(
            jwt,
            inviteToken ? { invite_token: inviteToken } : undefined,
          );
          // AuthCallbackScreen always uses Neon JWT (Google SSO / email verification)
          storage.setItem('cbjj_auth_provider', 'neon');
          if (inviteToken) {
            useAuthStore.getState().setPendingInviteToken(null);
          }
          result = syncData;
        } else {
          // Native: no sync result and no verifier → shouldn't happen, go to login
          clearAuth();
          setStep('error');
          setError(t('auth.callbackNoVerifier'));
          return;
        }

        setSyncResult(result);

        // Every path that reaches this point is a completed OAuth sign-in
        // (Google validated the email) or an email-verification callback —
        // both prove control of the email. We deliberately do NOT consult
        // getSession() here: reading it immediately after account creation
        // races the provider's bookkeeping and could return a stale `false`,
        // which used to strand brand-new Google users on the OTP screen.

        // Check if user needs to complete profile (SSO users won't have dob/terms/name)
        const needsCompletion =
          !result.user.dob ||
          !result.user.terms_accepted_at ||
          !result.user.first_name ||
          !result.user.last_name;

        // Set auth immediately (so JWT is available for API calls) with onboarding flag
        setAuth(result.access_token, result.user, needsCompletion, true);
        setActiveRole(result.user.role);

        if (needsCompletion) {
          // Pre-fill DOB for pre-provisioned sub-users (already has dob set by guardian)
          if (result.user.dob) {
            try {
              setDateOfBirth(new Date(result.user.dob));
            } catch {}
          }
          // Pre-fill name fields if the user already has them
          if (result.user.first_name) setFirstName(result.user.first_name);
          if (result.user.last_name) setLastName(result.user.last_name);
          setStep('needs_completion');
        } else {
          setStep('done');
        }
      } catch (err: any) {
        console.error('[AuthCallback] Error:', err);
        clearAuth();
        setError(err.message || t('auth.callbackError'));
        setStep('error');
      }
    };

    processCallback();
  }, []);

  // Navigate away once done
  useEffect(() => {
    if (step === 'done') {
      // RootNavigator will auto-switch to Main since isFullyAuthenticated is now true
      // (needsOnboarding was already set to false or was never true)
    }
  }, [step]);

  const handleCompleteProfile = () => {
    const newErrors: Record<string, string> = {};

    // Name is required if the user doesn't already have one
    const hasExistingName = !!(syncResult?.user?.first_name && syncResult?.user?.last_name);
    if (!hasExistingName) {
      if (!firstName.trim()) newErrors.firstName = t('auth.firstNameRequired');
      if (!lastName.trim()) newErrors.lastName = t('auth.lastNameRequired');
    }
    if (!isDobPrefilled && !dateOfBirth) {
      newErrors.dob = t('auth.dateOfBirthRequired');
    }
    if (dateOfBirth && !isAdult(dateOfBirth)) {
      newErrors.dob = t('auth.ageRequirement');
    }
    if (!termsChecked) {
      newErrors.terms = t('auth.termsRequired');
    }

    if (Object.keys(newErrors).length > 0) {
      setDobError(newErrors.dob || '');
      setNameError(newErrors.firstName || newErrors.lastName || '');
      if (newErrors.terms) setError(newErrors.terms);
      return;
    }
    setNameError('');

    const dobStr = dateOfBirth ? dateOfBirth.toISOString().split('T')[0] : undefined;
    setStep('completing');

    // Convert weight/height to canonical units (kg/cm) for storage
    const wVal = parseFloat(weightInput);
    const hVal = parseFloat(heightInput);
    const weightKg = !isNaN(wVal) ? weightToKg(wVal, unitSystem) : null;
    const heightCm = !isNaN(hVal) ? heightToCm(hVal, unitSystem) : null;

    // Update user profile with name, date_of_birth, terms acceptance, and body measurements
    updateProfile.mutate(
      {
        // Only send name fields if the user didn't have one already
        ...(hasExistingName ? {} : {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        }),
        dob: dobStr,
        terms_accepted: true,
        weight_kg: weightKg,
        height_cm: heightCm,
        measurement_unit: unitSystem,
      },
      {
        onSuccess: async (updatedUser) => {
          // Clear onboarding flag
          setNeedsOnboarding(false);

          // Check if user has an active membership
          try {
            const membershipCheck = await paymentsApi.getActiveMembership();
            setNeedsSubscription(!membershipCheck.has_active_membership);
          } catch {
            // If membership check fails, assume subscription needed
            setNeedsSubscription(true);
          }
        },
        onError: (err: any) => {
          setError(getApiError(err, t('auth.profileUpdateFailed'), t));
          setStep('needs_completion');
        },
      },
    );
  };

  // ─── Processing state ─────────────────────────────────────────────
  if (step === 'processing') {
    return (
      <View style={[styles.fullscreen, { backgroundColor: colors.bg }]}>
        <ActivityIndicator size="large" color={colors.accent} />
        <Text style={[styles.processingText, { color: colors.text }]}>
          {t('auth.authenticating')}
        </Text>
      </View>
    );
  }

  // ─── Error state ──────────────────────────────────────────────────
  if (step === 'error') {
    return (
      <View style={[styles.fullscreen, { backgroundColor: colors.bg }]}>
        <Text style={[styles.errorTitle, { color: colors.error }]}>
          {t('auth.authenticationFailed')}
        </Text>
        <Text style={[styles.errorMessage, { color: colors.textSecondary }]}>
          {error}
        </Text>
        <Button
          mode="contained"
          onPress={() => {
            clearAuth();
            // Navigate back to Login within the Auth stack
            navigation.replace('Login');
          }}
          style={[styles.button, { backgroundColor: colors.accent }]}
          textColor={colors.accentText}
        >
          {t('auth.backToSignIn')}
        </Button>
      </View>
    );
  }

  // ─── Needs email verification state ────────────────────────────────
  if (step === 'needs_email_verification') {
    const email = syncResult?.user?.email || '';
    return (
      <View style={[styles.fullscreen, { backgroundColor: colors.bg }]}>
        <Text style={[styles.title, { color: colors.text }]}>
          {t('auth.verifyEmail')}
        </Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          {t('auth.verifyEmailSubtitle', { email })}
        </Text>
        <Button
          mode="contained"
          onPress={() => {
            navigation.replace('EmailVerification', { email });
          }}
          style={[styles.button, { backgroundColor: colors.accent }]}
          textColor={colors.accentText}
        >
          {t('auth.goToVerification')}
        </Button>
      </View>
    );
  }

  // ─── Needs completion state (birthday + terms) ────────────────────
  if (step === 'needs_completion' || step === 'completing') {
    return (
      <KeyboardAvoidingView
        style={[styles.fullscreen, { backgroundColor: colors.bg }]}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.formContainer}>
            <Text style={[styles.title, { color: colors.text }]}>
              {t('auth.completeYourProfile')}
            </Text>
            <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
              {t('auth.completeProfileSubtitle')}
            </Text>

            {/* Name fields — only show if the user doesn't already have a name */}
            {!(syncResult?.user?.first_name && syncResult?.user?.last_name) && (
              <View style={styles.row}>
                <PaperTextInput
                  label={t('auth.firstName')}
                  value={firstName}
                  onChangeText={(v) => { setFirstName(v); if (nameError) setNameError(''); }}
                  mode="outlined"
                  style={[styles.input, styles.halfInput]}
                  outlineColor={colors.border}
                  activeOutlineColor={colors.text}
                  textColor={colors.text}
                  theme={{ colors: { onSurfaceVariant: colors.textSecondary, surface: colors.card } }}
                />
                <PaperTextInput
                  label={t('auth.lastName')}
                  value={lastName}
                  onChangeText={(v) => { setLastName(v); if (nameError) setNameError(''); }}
                  mode="outlined"
                  style={[styles.input, styles.halfInput]}
                  outlineColor={colors.border}
                  activeOutlineColor={colors.text}
                  textColor={colors.text}
                  theme={{ colors: { onSurfaceVariant: colors.textSecondary, surface: colors.card } }}
                />
              </View>
            )}
            {nameError ? (
              <Text style={[styles.fieldError, { color: colors.error }]}>
                {nameError}
              </Text>
            ) : null}

            {!isDobPrefilled && (
              <>
                <DatePicker
                  label={t('auth.dateOfBirth')}
                  value={dateOfBirth}
                  onChange={(date) => {
                    setDateOfBirth(date);
                    if (dobError) setDobError('');
                  }}
                  error={!!dobError}
                  maximumDate={new Date()}
                  minimumDate={new Date(1900, 0, 1)}
                />
                {dobError ? (
                  <Text style={[styles.fieldError, { color: colors.error }]}>
                    {dobError}
                  </Text>
                ) : null}
              </>
            )}

            {/* Body Measurements (optional) */}
            <Text style={[styles.fieldHint, { color: colors.textSecondary }]}>
              {t('profile.bodyMeasurementsHint')}
            </Text>
            <View style={styles.row}>
              <PaperTextInput
                label={`${t('profile.weight')} (${unitSystem === 'metric' ? 'kg' : 'lbs'})`}
                value={weightInput}
                onChangeText={setWeightInput}
                mode="outlined"
                keyboardType="decimal-pad"
                style={[styles.input, { flex: 1 }]}
                outlineColor={colors.border}
                activeOutlineColor={colors.text}
                textColor={colors.text}
              />
              <PaperTextInput
                label={`${t('profile.height')} (${unitSystem === 'metric' ? 'cm' : 'in'})`}
                value={heightInput}
                onChangeText={setHeightInput}
                mode="outlined"
                keyboardType="decimal-pad"
                style={[styles.input, { flex: 1 }]}
                outlineColor={colors.border}
                activeOutlineColor={colors.text}
                textColor={colors.text}
              />
              <TouchableOpacity
                style={[styles.unitToggleBtn, { alignSelf: 'center' }]}
                onPress={() => setUnitSystem(unitSystem === 'metric' ? 'imperial' : 'metric')}
              >
                <Text style={[styles.unitToggleText, { color: colors.accent }]}>
                  {unitSystem === 'metric' ? t('profile.metric') : t('profile.imperial')}
                </Text>
              </TouchableOpacity>
            </View>

            <View style={styles.termsRow}>
              {Platform.OS === 'ios' ? (
                <Switch
                  value={termsChecked}
                  onValueChange={(v) => {
                    setTermsChecked(v);
                    if (error) setError(null);
                  }}
                  trackColor={{ false: colors.border, true: colors.accent }}
                  thumbColor={termsChecked ? colors.accentText : colors.textSecondary}
                  style={{ marginRight: 8 }}
                />
              ) : (
                <Checkbox
                  status={termsChecked ? 'checked' : 'unchecked'}
                  onPress={() => {
                    setTermsChecked(!termsChecked);
                    if (error) setError(null);
                  }}
                  color={colors.accent}
                />
              )}
              <Text style={[styles.termsText, { color: colors.text }]}>
                {t('auth.termsPrefix')}{' '}
                <Text
                  style={[styles.termsLink, { color: colors.accent }]}
                  onPress={() => navigation.navigate('PublicLegalDoc', { docType: 'privacy_policy' })}
                >
                  {t('auth.privacyPolicy')}
                </Text>
                {' '}{t('auth.termsAnd')}{' '}
                <Text
                  style={[styles.termsLink, { color: colors.accent }]}
                  onPress={() => navigation.navigate('PublicLegalDoc', { docType: 'terms_of_service' })}
                >
                  {t('auth.termsOfService')}
                </Text>
              </Text>
            </View>

            {error && step !== 'completing' && (
              <Text style={[styles.fieldError, { color: colors.error, textAlign: 'center' }]}>
                {error}
              </Text>
            )}

            <TouchableOpacity
              onPress={handleCompleteProfile}
              disabled={step === 'completing'}
              style={[styles.button, { backgroundColor: colors.accent }]}
              activeOpacity={0.8}
            >
              {step === 'completing' ? (
                <ActivityIndicator size="small" color={colors.accentText} />
              ) : (
                <Text style={[styles.buttonText, { color: colors.accentText }]}>
                  {t('auth.continueToApp')}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    );
  }

  // ─── Done state (brief flash before navigation) ───────────────────
  return (
    <View style={[styles.fullscreen, { backgroundColor: colors.bg }]}>
      <ActivityIndicator size="large" color={colors.accent} />
      <Text style={[styles.processingText, { color: colors.text }]}>
        {t('auth.authenticating')}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  fullscreen: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  processingText: {
    marginTop: 16,
    fontSize: 16,
    fontWeight: '500',
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 24,
    paddingHorizontal: 16,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  formContainer: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 32,
    paddingHorizontal: 16,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 8,
  },
  input: {
    marginBottom: 8,
    backgroundColor: 'transparent',
  },
  fieldError: {
    marginTop: -4,
    marginBottom: 8,
    marginLeft: 4,
    fontSize: 12,
  },
  fieldHint: {
    fontSize: 12,
    marginBottom: 8,
    marginLeft: 4,
  },
  termsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 8,
    marginBottom: 16,
  },
  termsText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    paddingTop: 6,
  },
  termsLink: {
    textDecorationLine: 'underline',
    fontWeight: '500',
  },
  button: {
    marginTop: 8,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  halfInput: {
    flex: 1,
  },
  unitToggleBtn: {
    paddingHorizontal: 12,
    paddingVertical: 14,
    borderRadius: 8,
    backgroundColor: 'transparent',
  },
  unitToggleText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
